import { motion } from 'framer-motion'
import { Shield, ArrowRight, Lock, Cloud, Settings, Eye, Check, Server } from 'lucide-react'
import { privacyFeatures } from '../../data/frameworks'

interface StepProps {
  onNext: () => void
}

const Step4 = ({ onNext }: StepProps) => {
  const privacyIcons = {
    encryption: Lock,
    'local-processing': Settings,
    'data-control': Cloud,
    'no-training': Shield
  }

  const dataFlowSteps = [
    { label: 'Your Data', icon: Eye, color: 'from-blue-500 to-cyan-500' },
    { label: 'Encrypted', icon: Lock, color: 'from-green-500 to-emerald-500' },
    { label: 'Processed', icon: Server, color: 'from-purple-500 to-pink-500' },
    { label: 'Returned', icon: Shield, color: 'from-orange-500 to-amber-500' }
  ]

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', duration: 0.6 }}
        className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mb-6 shadow-xl shadow-primary-500/30"
      >
        <Shield className="w-10 h-10 text-white" />
      </motion.div>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-3xl md:text-4xl font-bold text-center mb-4"
      >
        <span className="gradient-text">Privacy First</span>
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-lg text-surface-600 dark:text-surface-400 text-center max-w-2xl mb-12"
      >
        Your personal data should remain personal. AssistantTwin is built from the ground up
        with privacy and security as core principles, not afterthoughts.
      </motion.p>

      {/* Data Flow Visualization */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="w-full max-w-3xl mb-12"
      >
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-surface-900 dark:text-surface-100 mb-6 text-center">
            How Your Data Flows
          </h3>
          <div className="flex items-center justify-between relative">
            {/* Connection Line */}
            <div className="absolute left-0 right-0 top-1/2 h-0.5 bg-gradient-to-r from-blue-500 via-green-500 via-purple-500 to-orange-500 -translate-y-1/2 z-0" />

            {dataFlowSteps.map((step, index) => (
              <motion.div
                key={step.label}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 + index * 0.2 }}
                className="relative z-10 flex flex-col items-center"
              >
                <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg mb-2`}>
                  <step.icon className="w-7 h-7 text-white" />
                </div>
                <span className="text-sm font-medium text-surface-600 dark:text-surface-400">
                  {step.label}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Privacy Features Grid */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mb-8"
      >
        {privacyFeatures.map((feature, index) => {
          const Icon = privacyIcons[feature.id as keyof typeof privacyIcons] || Shield
          return (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 + index * 0.1 }}
              className="card p-5 flex items-start gap-4"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Icon className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <h3 className="font-semibold text-surface-900 dark:text-surface-100 mb-1">
                  {feature.title}
                </h3>
                <p className="text-sm text-surface-600 dark:text-surface-400">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          )
        })}
      </motion.div>

      {/* Trust Indicators */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="flex flex-wrap justify-center gap-4 mb-12"
      >
        {[
          'SOC 2 Type II',
          'GDPR Compliant',
          'CCPA Compliant',
          'Zero-Knowledge Architecture'
        ].map((badge) => (
          <div
            key={badge}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 text-green-600 dark:text-green-400"
          >
            <Check className="w-4 h-4" />
            <span className="text-sm font-medium">{badge}</span>
          </div>
        ))}
      </motion.div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        onClick={onNext}
        className="btn-primary text-lg group"
      >
        Try Interactive Demo
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </motion.button>
    </div>
  )
}

export default Step4
